import type { SeoWarning } from '@nucleus-commerce/api-client/admin';

/** The backend's limits for the resolved title and description (doc 15). */
export const SEO_TITLE_MAX = 60;
export const SEO_DESCRIPTION_MAX = 160;

/** What the store settings contribute to every resolved SEO object. */
export interface SeoStoreContext {
  name: string;
  titleTemplate: string;
  defaultMetaDescription: string | null;
  indexable: boolean;
  primaryDomain: string | null;
}

export interface SeoOverrideValues {
  title: string;
  description: string;
  canonical: string;
  noIndex: boolean;
}

export interface SeoPreviewInput {
  title: string;
  /** Plain text or HTML the entity would be described by without an override. */
  description: string | null;
  /** Store path, e.g. `/pages/about`. */
  path: string;
  /** Published and live, so the storefront would index it. */
  published: boolean;
  override: SeoOverrideValues;
  /** Null skips the image checks (pages carry no image). */
  image: { alt: string | null } | 'none' | null;
}

export interface SeoPreview {
  title: string;
  description: string | null;
  canonical: string | null;
  robots: 'index, follow' | 'noindex, follow';
  warnings: SeoWarning[];
}

/** Same rules as the backend's `stripHtml`, so the preview matches what the storefront gets. */
export function stripHtml(html: string): string {
  return html
    .replace(/<\/?(p|div|br|li|ul|ol|h[1-6]|tr|td|th|section|article|blockquote)[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cuts on a word boundary; a single very long word is hard-cut (backend `truncate`). */
export function truncateWords(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max + 1);
  const at = slice.lastIndexOf(' ');
  return (at > max * 0.5 ? slice.slice(0, at) : text.slice(0, max)).replace(/[\s,;:.-]+$/, '');
}

export function storeBaseUrl(primaryDomain: string | null): string | null {
  return primaryDomain ? `https://${primaryDomain}` : null;
}

/**
 * The resolved SEO object as the backend would compute it from the unsaved form, so the editor
 * sees the storefront's title, description and robots while typing. Mirrors `resolveSeo` in the
 * backend's structured-data module; the saved entity's `resolvedSeo` stays the authority.
 */
export function resolveSeoPreview(input: SeoPreviewInput, store: SeoStoreContext): SeoPreview {
  const warnings: SeoWarning[] = [];
  const override = input.override;
  const rawTitle =
    override.title.trim() ||
    store.titleTemplate.replace('{title}', input.title).replace('{store}', store.name);
  if (!input.title.trim()) warnings.push('title_missing');
  if (rawTitle.length > SEO_TITLE_MAX) warnings.push('title_too_long');
  const ownDescription = input.description ? stripHtml(input.description) : '';
  const rawDescription =
    override.description.trim() || ownDescription || (store.defaultMetaDescription ?? '');
  if (!rawDescription) warnings.push('description_missing');
  else if (rawDescription.length > SEO_DESCRIPTION_MAX) warnings.push('description_too_long');
  if (input.image === 'none') warnings.push('image_missing');
  else if (input.image && !input.image.alt) warnings.push('alt_missing');
  const indexable = input.published && store.indexable && !override.noIndex;
  const base = storeBaseUrl(store.primaryDomain);
  return {
    title: truncateWords(rawTitle, SEO_TITLE_MAX),
    description: rawDescription ? truncateWords(rawDescription, SEO_DESCRIPTION_MAX) : null,
    canonical: override.canonical.trim() || (base ? `${base}${input.path}` : null),
    robots: indexable ? 'index, follow' : 'noindex, follow',
    warnings,
  };
}
